import React, { useState, useEffect } from 'react';
import { track } from '@vercel/analytics';

const DownloadButton = ({ timelineData }) => {
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);

  // Handle download requests from mobile dropdown
  useEffect(() => {
    const handleDownloadRequest = (event) => {
      if (event.detail) {
        handleDownloadClick();
      }
    };

    window.addEventListener('downloadRequested', handleDownloadRequest);
    return () => {
      window.removeEventListener('downloadRequested', handleDownloadRequest);
    };
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setIsValidEmail(validateEmail(value) || value === '');
  };

  const handleDownloadClick = () => {
    // Track the download button click
    track('download_button_clicked', {
      timestamp: new Date().toISOString(),
      hasData: timelineData && timelineData.length > 0
    });
    setShowModal(true);
  };

  const handleDownload = async () => {
    if (!email.trim()) {
      setIsValidEmail(false);
      return;
    }

    if (!validateEmail(email)) {
      setIsValidEmail(false);
      return;
    }

    // Track successful download with email
    track('data_downloaded', {
      email: email,
      timestamp: new Date().toISOString(),
      dataCount: timelineData ? timelineData.length : 0
    });

    // Save email to Supabase
    try {
      const response = await fetch('/api/save-download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email
        })
      });
    } catch (error) {
      // Silently fail - download will still work
    }

    // Create CSV content
    const csvContent = [
      ['Meeting Date', 'Meeting Title', 'Meeting Location', 'Meeting Summary', 'Meeting Participants'],
      ...timelineData.map(item => [
        item.date,
        item.title,
        item.subtitle,
        item.description,
        // Format participants array into a readable string
        item.participants && item.participants.length > 0
          ? item.participants.map(p => 
              `${p.name} (${p.roles?.[0] || 'No role'}, ${p.affiliations?.[0] || 'No organization'}, ${p.countries?.[0] || 'No country'})`
            ).join('; ')
          : 'No participants'
      ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'jalisco-timeline-data.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Close modal and reset form
    setShowModal(false);
    setEmail('');
    setIsValidEmail(true);
  };

  const handleCancel = () => {
    setShowModal(false);
    setEmail('');
    setIsValidEmail(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleDownload();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <>
      <button 
        onClick={handleDownloadClick}
        className="download-button"
        title="Download timeline data as CSV"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
        </svg>
        Download Data
      </button>

      {showModal && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Download Timeline Data</h3>
              <button 
                className="modal-close" 
                onClick={handleCancel}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Please enter your email address to download the timeline data:</p>
              <div className="email-input-container">
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onKeyDown={handleKeyPress}
                  placeholder="Enter your email address"
                  className={`email-input ${!isValidEmail ? 'error' : ''}`}
                  autoFocus
                />
                {!isValidEmail && (
                  <span className="error-message">
                    Please enter a valid email address
                  </span>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-cancel" 
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button 
                className="modal-download" 
                onClick={handleDownload}
                disabled={!email.trim() || !isValidEmail}
              >
                Download CSV
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DownloadButton;
